import type { RealtimeEvents } from '@vmc/shared';
import { createContext, use, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getAccessToken, refreshSession } from './session';

type ServerEvents = { [K in keyof RealtimeEvents]: (payload: RealtimeEvents[K]) => void };
interface ClientEvents {
  'conversation:join': (payload: { conversationId: string }) => void;
  'conversation:leave': (payload: { conversationId: string }) => void;
}
export type RealtimeSocket = Socket<ServerEvents, ClientEvents>;

interface RealtimeContextValue {
  socket: RealtimeSocket | null;
  connected: boolean;
}

const RealtimeContext = createContext<RealtimeContextValue>({ socket: null, connected: false });

function createSocket(): RealtimeSocket {
  return io('/realtime', {
    path: '/socket.io',
    transports: ['websocket'],
    autoConnect: false,
    auth: (cb) => cb({ token: getAccessToken() }),
  });
}

/**
 * One Socket.IO connection to `/realtime` per signed-in tab. `auth` is a callback, so every
 * (re)connect reads the latest in-memory token. When the server drops us for an expired token
 * we refresh first and reconnect manually, because Socket.IO doesn't auto-reconnect after a
 * server-initiated disconnect or a rejected handshake.
 *
 * `enabled` is read once; the shell is remounted per user, so it never changes for a socket.
 */
export function RealtimeProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [socket] = useState(() => (enabled ? createSocket() : null));
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!socket) return undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const reconnectWithFreshToken = (delayMs: number) => {
      clearTimeout(retryTimer);
      retryTimer = setTimeout(() => {
        refreshSession()
          .then((session) => {
            if (session) socket.connect();
            return session;
          })
          // API unreachable: try again later.
          .catch(() => reconnectWithFreshToken(Math.min(delayMs * 2, 30_000)));
      }, delayMs);
    };

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', (reason) => {
      setConnected(false);
      if (reason === 'io server disconnect') reconnectWithFreshToken(250);
    });
    socket.on('connect_error', () => {
      setConnected(false);
      // `active` is false when the server rejected the handshake (bad/expired token).
      if (!socket.active) reconnectWithFreshToken(1_000);
    });
    socket.connect();

    return () => {
      clearTimeout(retryTimer);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [socket]);

  return <RealtimeContext value={{ socket, connected }}>{children}</RealtimeContext>;
}

export const useRealtime = (): RealtimeContextValue => use(RealtimeContext);

/** Subscribes to a server event; the handler may change every render without re-subscribing. */
export function useRealtimeEvent<K extends keyof RealtimeEvents>(
  event: K,
  handler: (payload: RealtimeEvents[K]) => void,
): void {
  const { socket } = useRealtime();
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  useEffect(() => {
    if (!socket) return undefined;
    const listener = (payload: RealtimeEvents[K]) => ref.current(payload);
    // Socket.IO's generic overloads can't correlate K with the listener type, so use the untyped view.
    const s = socket as unknown as Socket;
    const name: string = event;
    s.on(name, listener);
    return () => {
      s.off(name, listener);
    };
  }, [socket, event]);
}
